/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomáš Burian (burian@cadwork.cz)
 *
 */

/**
 * @param {cadwork.webgl.SceneAPI.Scene} aSceneAPI
 * @extends cadwork.webgl.SceneAPI.elements.Element
 * @constructor
 */
import {DataStorage, EEmbededElementTypes, RenderDocument} from "js_renderer";
import Element from "./element";
import Factory from "./factory";
import {vec3} from "gl-matrix";

export default
class Tube extends Element {

    public static TypeName: string = EEmbededElementTypes.TUBE;
    protected _url: string;
    protected _parameters: { phiStart: number; heightSegments: number; thetaLength: number; phiLength: number; radius: number; widthSegments: number; thetaStart: number };
    protected _positions: Float32Array;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);
        // 360 / 8 = step is 45° * 2 because of the top and bottom points of the tube
        this._positions = null;
        this._properties.color = vec3.fromValues(1.0, 1.0, 1.0);
    }

    public static create(aRenderDocument: RenderDocument): Tube {
        let element = new Tube(aRenderDocument);
        element._typeName = Tube.TypeName;
        return element;
    }

    public static getAllPositions(aCenter: vec3, aLength: number, aRadius: number, aSegmentsCount: number): Float32Array {
        let positions = new Float32Array(aSegmentsCount * 2 * 3);

        // top positions
        for (let i = 0; i < aSegmentsCount; i++) {
            positions[i * 3 + 0] = (aCenter[0] + aRadius * Math.cos(2 * Math.PI * i / aSegmentsCount));
            positions[i * 3 + 1] = aCenter[1] - (aLength / 2.0);
            positions[i * 3 + 2] = (aCenter[2] + aRadius * Math.sin(2 * Math.PI * i / aSegmentsCount));
        }

        // bottom positions
        for (let i = aSegmentsCount; i < 2 * aSegmentsCount; i++) {
            positions[i * 3 + 0] = (aCenter[0] + aRadius * Math.cos(2 * Math.PI * i / aSegmentsCount));
            positions[i * 3 + 1] = aCenter[1] + (aLength / 2.0);
            positions[i * 3 + 2] = (aCenter[2] + aRadius * Math.sin(2 * Math.PI * i / aSegmentsCount));
        }

        return positions;
    }

    public setPositions(aPositions: Float32Array) {
        if (aPositions.slice !== undefined) {
            this._positions = new Float32Array(aPositions.slice());
        } else {
            this._positions = new Float32Array(aPositions.length);
            for (let i = 0; i < aPositions.length; i++) {
                this._positions[i] = aPositions[i];
            }
        }

        this.updatePositions([
            this._positions
        ]);
    }

    protected _generateElementDescriptionJSON(): any {
        return {
            "type": this.getTypeName(),
            "id": this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            "UID": this._UID
        };
    }

    private _computeIndices(): number[] {
        let segmentsCount = this._positions.length / (2 * 3);
        let indices = [];

        let i = 0;
        for (i = 0; i < segmentsCount - 1; i++) {
            indices.push(i);
            indices.push(i + segmentsCount);
            indices.push(i + segmentsCount + 1);

            indices.push(i + segmentsCount + 1);
            indices.push(i);
            indices.push(i + 1);
        }

        // close the ring
        indices.push(i);
        indices.push(i + segmentsCount);
        indices.push(0 + segmentsCount);

        indices.push(0 + segmentsCount);
        indices.push(i);
        indices.push(0);

        return indices;
    }

    protected _generateGeometryObjectDescriptionJSON(): any {
        let appearanceContent: any = {};

        appearanceContent.material = {
            diffuseColor: vec3.clone(this._properties.color),
            ambientColor: vec3.fromValues(1.0, 1.0, 1.0),
            transparency: this._properties.transparency
        };

        appearanceContent.renderOption = {
            vertexOrdering: "UNKNOWN",
            clippable: this._properties.clippable
        };

        return [{
            description: {
                type: "SHAPE",
                bboxMin: [this._positions[0], this._positions[1], this._positions[2]],
                bboxMax: [this._positions[6], this._positions[7], this._positions[8]],
                contributeToNearPlaneClipping: false
            },
            appearance: appearanceContent,
            indexedFaceSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: 24 * 2,
                normalCount: 0,
                texCoordCount: 0,
                indexCount: 23 * 2 * 3,
                binSize: 0,
                positions: this._positions,
                indices: this._computeIndices()
            }
        }];
    }
}

Factory.register(Tube);
